import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

// Configuration constants
const GAP = 20;
const MIN_CARD_WIDTH = 280; // Minimum width before dropping to fewer columns
const MAX_COLUMNS = 4;      // Max cards to show at once
const DEBOUNCE_TIME = 100;
const SCROLL_ANIMATION_TIME = 300;

const HorizontalCardDeck = ({ 
    items, 
    renderItem, 
}) => {
    const scrollContainerRef = useRef(null);
    
    // State
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(false);
    const [isButtonClickScroll, setIsButtonClickScroll] = useState(false);
    
    // Layout State
    const [cardsPerView, setCardsPerView] = useState(1);
    const [cardWidth, setCardWidth] = useState(MIN_CARD_WIDTH);

    // Refs
    const scrollTimeoutRef = useRef(null); 

    // --- RESPONSIVE LAYOUT LOGIC ---
    const calculateLayout = useCallback(() => {
        if (!scrollContainerRef.current) return;
        
        const containerWidth = scrollContainerRef.current.clientWidth;
        
        let possibleColumns = Math.floor((containerWidth + GAP) / (MIN_CARD_WIDTH + GAP));
        const columns = Math.max(1, Math.min(possibleColumns, MAX_COLUMNS));

        const totalGapSpace = GAP * (columns - 1);
        const availableSpace = containerWidth - totalGapSpace;
        const exactCardWidth = availableSpace / columns;

        setCardsPerView(columns);
        setCardWidth(exactCardWidth);
    }, []);

    // --- SCROLL & POSITION LOGIC ---
    const checkScrollPosition = useCallback(() => {
        if (!scrollContainerRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        
        const maxScrollLeft = scrollWidth - clientWidth - 2;
        
        setShowLeftButton(scrollLeft > 2); 
        setShowRightButton(scrollLeft < maxScrollLeft);
    }, []);

    const getScrollStep = useCallback(() => {
        return (cardWidth * cardsPerView) + (GAP * (cardsPerView - 1));
    }, [cardWidth, cardsPerView]);

    const scroll = useCallback(direction => {
        if (!scrollContainerRef.current) return;
        
        setIsButtonClickScroll(true); 

        const step = getScrollStep();
        const container = scrollContainerRef.current;
        const current = container.scrollLeft;
        const maxScrollLeft = container.scrollWidth - container.clientWidth; 

        let targetLeft;
        if (direction === 'left') {
            targetLeft = Math.max(0, current - step); 
        } else {
            targetLeft = Math.min(maxScrollLeft, current + step); 
        }

        container.scrollTo({
            left: targetLeft,
            behavior: 'smooth'
        });

        setTimeout(() => {
            setIsButtonClickScroll(false);
            checkScrollPosition(); 
        }, SCROLL_ANIMATION_TIME);
    }, [getScrollStep, checkScrollPosition]);

    const snapToNearest = useCallback(() => {
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        
        const singleCardStep = cardWidth + GAP;
        const index = Math.round(container.scrollLeft / singleCardStep);
        const targetScroll = index * singleCardStep;

        if (Math.abs(container.scrollLeft - targetScroll) > 10) {
             container.scrollTo({ left: targetScroll, behavior: 'smooth' });
        }
    }, [cardWidth]);

    // --- EFFECTS ---

    // 1. Initial Layout & Resize (UNCHANGED)
    useEffect(() => {
        calculateLayout();
        const handleResize = () => {
            setTimeout(() => {
                calculateLayout();
                checkScrollPosition();
            }, DEBOUNCE_TIME);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateLayout, checkScrollPosition]);

    // 2. Scroll Listener (MODIFIED - Removed Touch Handlers)
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            checkScrollPosition();
            clearTimeout(scrollTimeoutRef.current);
            if (!isButtonClickScroll) { 
                scrollTimeoutRef.current = setTimeout(snapToNearest, 100);
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
        
        checkScrollPosition();

        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeoutRef.current);
        };
    }, [checkScrollPosition, isButtonClickScroll, snapToNearest]);

    // --- RENDER ---
    const renderContent = useMemo(() => {
        return items.map((item, index) => (
            <div
                key={index}
                className="flex-shrink-0" 
                style={{ 
                    width: `${cardWidth}px`,
                    scrollSnapAlign: 'start',
                }}
            >
                {renderItem(item)} 
            </div>
        ));
    }, [items, cardWidth, renderItem]);

    return (
        <div className="relative w-full">
            {/* Left Button - ADDED border-none */}
            {showLeftButton && (
                <button
                    onClick={() => scroll('left')}
                    className="scroll-button left dark:scroll-button-dark flex-shrink-0 border-none"
                    aria-label="Scroll left"
                    style={{ zIndex: 20 }}
                >
                    <i className="bi bi-chevron-left dark:text-white"></i>
                </button>
            )}

            <div
                ref={scrollContainerRef}
                className="flex overflow-x-auto overflow-y-hidden scrollbar-hide"
                style={{
                    gap: `${GAP}px`,
                    scrollSnapType: 'x mandatory', 
                    paddingTop: '10px', 
                    paddingBottom: '40px', 
                    marginBottom: '-20px',
                }}
            >
                {renderContent}
            </div>

            {/* Right Button - ADDED border-none */}
            {showRightButton && (
                <button
                    onClick={() => scroll('right')}
                    className="scroll-button right dark:scroll-button-dark flex-shrink-0 border-none"
                    aria-label="Scroll right"
                    style={{ zIndex: 20 }}
                >
                    <i className="bi bi-chevron-right dark:text-white"></i>
                </button>
            )}
        </div>
    );
};

export default React.memo(HorizontalCardDeck);